import React, {useRef, useState} from "react";
import {getModelFactory} from "../../../ts/model-factory/ModelFactory";
import {getManager} from "../../../ts/manager/Manager";
import {
    LocateDataChoice,
    LocateDataModel, LocateSearchParams
} from "../../../ts/data/locate/LocateDataFactory";
import NoContent from "../../partial/status/NoContent";
import AppButton from "../../element/button/AppButton";
import StateViewController from "../../vc/StateViewController";
import {IRouteProps} from "../../../ts/manager/view/ViewInterface";
import {dispatchRedirectTo} from "../../../ts/event/DispatchEvent";
import {IonSpinner} from "@ionic/react";
import LocateMapForm from "../../form/locate/LocateMapForm";
import {LngLat} from "maplibre-gl";
import {IMapClusterProps} from "../../../module/osm/element/MapCluster";
import MapLibreMap, {MapLibreMapInstance, MapFocus} from "../../../module/osm/MapLibreMap";
import {IMarkerProps, IMarkersProps, MapMarkerInstance} from "../../../module/osm/element/MapMarker";
import {IMapPopupProps} from "../../../module/osm/element/MapPopup";
import {ILocatableDataChoices, LocatableDataChoice} from "../../../ts/data/locate/LocatableDataFactory";
import {CountryCode} from "../../../module/osm/location/LocationType";
import {primaryCountryCode} from "../../../module/osm/config/MapCoreConfig";

interface IProps extends IRouteProps
{
    getCardContent:(locateChoice:LocatableDataChoice, fromLngLat:LngLat|null, index:number, tally:number) => React.JSX.Element|null;
    primaryMarker?:LocatableDataChoice|null
    getPrimaryMarkerCardContent?:() => React.JSX.Element|null;
    getUserCardContent?:() => React.JSX.Element|null;
}

const LocateMapPage = (props:IProps) =>
{
    const [defaultCountryCode, setDefaultCountryCode] = useState<string>("");
    const mapRef = useRef<MapLibreMapInstance|null>(null);
    const [, setSimpleState] = useState<any[]>([]);
    const [countryChoiceOrder, _] = useState<CountryCode[]>([]);
    const softMarkerLimit = 150;


    let [referenceFoundChoiceIDs, setReferenceFoundChoiceIDs] = useState<string[]>([]);
    let [userMarkerProps, setUserMarkerProps] = useState<IMarkerProps|null>(null);
    let [primaryMarkerProps, setPrimaryMarkerProps] = useState<IMarkerProps|null>(null);
    let [mapClusterProps, setMapClusterProps] = useState<IMapClusterProps|null>(null);
    let [popupProps, setPopupPropsProps] = useState<IMapPopupProps|null>(null);

    const postType = getManager().navigation.model.routeIDs.post_type ?? null;

    const countrySearchParams:LocateSearchParams = new LocateSearchParams();
    countrySearchParams.postType = postType ?? "organisation";
    countrySearchParams.requireLocation = true;

    const locateCountryModel = getModelFactory().locateCountryData.getLocateCountryDataModel(countrySearchParams);

    let preferredMapFocusType:MapFocus = "cluster";
    let countryCode:CountryCode|"" = "";

    const determineCountry = () => {

        if(getManager().location.isUserLocationDetermined(true) && locateCountryModel.isPopulated()){

            let useDefaultCountryCode:CountryCode|"" = "";
            useDefaultCountryCode = getManager().location.lastKnownUserAddress?.countryCode ?? "";
            if(!locateCountryModel.choiceOrder.includes(useDefaultCountryCode)){
                useDefaultCountryCode = "";
            }
            if(!useDefaultCountryCode){
                useDefaultCountryCode = props.primaryMarker?.countryCode ?? "";
                if(!locateCountryModel.choiceOrder.includes(useDefaultCountryCode)){
                    useDefaultCountryCode = "";
                }
            }
            if(!useDefaultCountryCode){

                useDefaultCountryCode = primaryCountryCode;
                if(!locateCountryModel.choiceOrder.includes(useDefaultCountryCode)){
                    useDefaultCountryCode = "";
                }
            }
            if(!useDefaultCountryCode){
                useDefaultCountryCode = (locateCountryModel.choiceOrder[0] ?? "") as CountryCode|"";
            }
            if(useDefaultCountryCode && (useDefaultCountryCode !== defaultCountryCode)){
                setDefaultCountryCode(useDefaultCountryCode);
            }
            countryCode = decodeURIComponent(getManager().navigation.currentParams.country ?? "").toLowerCase() as CountryCode|"";
            if(!locateCountryModel.choiceOrder.includes(countryCode)){
                countryCode = useDefaultCountryCode;
            }

        }
    }

    determineCountry();

    const locateModel: LocateDataModel|null = getModelFactory().locateData.getMapLocateDataModel(
        postType,
        countryCode,
    );

    const viewState = StateViewController({
        routeProps: props,
        dataModels: [locateCountryModel, countryCode ? locateModel : null],
        promptForUserLocation: true
    })

    if(viewState.overrideView){
        return viewState.overrideView;
    }

    const countryChoiceList = locateCountryModel.choiceList();
    const userPosition = getManager().location.getLastKnowLngLat();

    if (!countryChoiceList.length || !countryCode)
    {
        return <NoContent {...props}/>;
    }
    const boundsChoiceOrder:string[] = [];
    const locateModels:LocateDataModel[] =[];
    const clusterMarkersProps:IMarkersProps = {}
    let referencesDidUpdate = false;

    const getMap = ():MapLibreMapInstance|null =>
    {
        return (mapRef.current as MapLibreMapInstance) ?? null;
    }

    const getChoiceModel = (choiceID:string): LocateDataModel|null =>
    {
        for(let locateModel of locateModels)
        {
            const choice = locateModel.choices[choiceID] ?? null;
            if(choice){
                return locateModel;
            }
        }
        return null;
    }

    const getChoice = (choiceID:string): LocatableDataChoice|null =>
    {
        const model = getChoiceModel(choiceID);
        return model?.choices[choiceID] ?? null;
    }

    const getChoiceMarkerID = (choiceID:string): string|null =>
    {
        const choice = getChoice(choiceID);
        return choice?.lngLat ? (choice.lngLat.lat + "," + choice.lngLat.lng) : null;
    }


    const getCardContent = (marker:MapMarkerInstance):React.JSX.Element =>
    {
        const choiceID = marker.getSelectedObjectID();
        const choiceModel = choiceID ? getChoiceModel(choiceID) : null;
        const choice = choiceModel?.choices[choiceID!] ?? null;
        let cardContent:React.JSX.Element|null = null;
        let referenceNotFound = false;
        if(choiceID && choiceModel && choice)
        {
            if(!(choice instanceof LocateDataChoice)){
                const choiceOrder = choiceModel.requireWithLocationChoiceOrder();
                cardContent = props.getCardContent(choice,userPosition, choiceOrder.indexOf(choiceID), choiceOrder.length);
            } else {
                referenceNotFound = true;
            }
        } else {
            referenceNotFound = true;
        }

        cardContent = cardContent ?? <>
            {!referenceNotFound ? <>
                <IonSpinner></IonSpinner>
            </> : <>
                Currently unavailable
            </>}
        </>

        return <div className="card-map">
            {cardContent}
        </div>
    }

    const onSearchSubmit = (countryCode:string) =>
    {
        const isDefaultCountryCode = countryCode === defaultCountryCode;
        let query = "";
        if(countryCode && !isDefaultCountryCode)
        {
            query += "country=" + countryCode;
        }
        const baseURL = props.choice.baseURL;
        let path = baseURL + "?" + query;
        dispatchRedirectTo(path)
    }

    const onMapInteraction = () => {
        setSimpleState([]);
    }

    const getLastSelectedBoundsChoiceIndex = ():number =>
    {
        const selectedMarker = getMap()?.selectedMarker ?? null;
        const choiceID = selectedMarker?.markerType === "cluster" ? selectedMarker.getSelectedObjectID() : null;
        return choiceID ? boundsChoiceOrder.indexOf(choiceID) : -1
    }

    const showPreviousEntry = () =>
    {
        const currentIndex = getLastSelectedBoundsChoiceIndex();
        const useIndex = currentIndex > 0 ?  currentIndex - 1 : boundsChoiceOrder.length - 1;
        const useChoiceID = useIndex >= 0 ? boundsChoiceOrder[useIndex] : null;
        const useMarkerID = useChoiceID ? getChoiceMarkerID(useChoiceID) : null;
        if(!useMarkerID){
            return;
        }
        getMap()?.selectClusterMarker(useMarkerID, useChoiceID, true, true);
    }

    const showNextEntry = () =>
    {
        const currentIndex = getLastSelectedBoundsChoiceIndex();
        const useIndex = currentIndex < boundsChoiceOrder.length - 1 ?  currentIndex + 1 : 0;
        const useChoiceID = useIndex >= 0 ? boundsChoiceOrder[useIndex] : null;
        const useMarkerID = useChoiceID ? getChoiceMarkerID(useChoiceID) : null;
        if(!useMarkerID){
            return;
        }
        getMap()?.selectClusterMarker(useMarkerID, useChoiceID, true, true);
    }

    const onMarkerSelected = (marker:MapMarkerInstance) => {
        const choiceID = marker.markerType === "cluster" ? marker.getSelectedObjectID() : null;
        if(choiceID){
            let choice:LocatableDataChoice|null = null;
            let locateModel:LocateDataModel|null = null;
            for(let checkLocateModel of locateModels){
                choice = checkLocateModel.choices[choiceID] ?? null;
                if(choice){
                    locateModel = checkLocateModel;
                    break;
                }
            }
            if(choice){
                locateModel!.requireReference(choice.id).then();
            }
        }
        setSimpleState([])
    }


    const prepareContent = ():void =>
    {


        const map = getMap();
        const allLocateChoices:ILocatableDataChoices = {};

        if(map && countryCode && map.countryLocation.countryCode !== countryCode){
            map.didFindActiveFocus = false;
        }

        if(countryCode){
            const defaultCountryIndex = countryChoiceOrder.indexOf(countryCode);
            if(defaultCountryIndex !== 0){
                if(defaultCountryIndex > 0){
                    countryChoiceOrder.splice(defaultCountryIndex, 1);
                }
                countryChoiceOrder.unshift(countryCode);
            }
            for(let altCountryCode of locateCountryModel.choiceOrder){
                if(!countryChoiceOrder.includes(altCountryCode as CountryCode)){
                    countryChoiceOrder.push(altCountryCode as CountryCode);
                }
            }
        }

        for(let altCountryCode of countryChoiceOrder){
            const altLocateModel = getModelFactory().locateData.getMapLocateDataModel(
                postType,
                altCountryCode,
            );
            if(!altLocateModel.isPopulated()){
                continue
            }
            locateModels.push(altLocateModel);
            const fromChoiceList = altLocateModel.withLocationChoiceList();

            for(let choice of fromChoiceList){
                if(!allLocateChoices[choice.id]){
                    if(!(choice instanceof LocateDataChoice)){
                        if(!referenceFoundChoiceIDs.includes(choice.id)){
                            referenceFoundChoiceIDs.push(choice.id);
                            referencesDidUpdate = true;
                        }
                    }

                    const markerID = choice.lngLat!.lat + "," + choice.lngLat!.lng;
                    let markerProps = clusterMarkersProps[markerID];
                    if(!markerProps){
                        if(softMarkerLimit > 0 && (altCountryCode !== countryCode) && (Object.keys(clusterMarkersProps).length >= softMarkerLimit)){
                            continue;
                        }

                        markerProps = {
                            marker: {
                                latitude: choice.lngLat!.lat,
                                longitude: choice.lngLat!.lng,
                            },
                            objectIDs: [],
                            popupContent: getCardContent
                        };

                        clusterMarkersProps[markerID] = markerProps;
                    }
                    markerProps.objectIDs!.push(choice.id);
                    if(altCountryCode === countryCode){
                        boundsChoiceOrder.push(choice.id)
                    }

                    markerProps.outOfBounds = altCountryCode !== countryCode;

                    allLocateChoices[choice.id] = choice;
                }
            }
        }
        if(referencesDidUpdate){
            setReferenceFoundChoiceIDs(referenceFoundChoiceIDs);
        }
    }

    const requirePopupProps = ():void =>
    {
        if(!referencesDidUpdate){
            return;
        }
        popupProps = {
            ...popupProps,
            contentVersion: (popupProps?.contentVersion ?? 0) + 1
        }
        setPopupPropsProps(popupProps);
    }

    const requireUserMarkerProps = ():void =>
    {
        if(userMarkerProps){
            return;
        }
        const cardContent = props.getUserCardContent ? props.getUserCardContent() : null
        const hasPopup = !props.getUserCardContent || !!cardContent;
        userMarkerProps = {
            marker: {
                latitude: 0,
                longitude: 0,
            },
            popupContent: hasPopup ?
                () => {
                    return <div className="card-map">{(props.getUserCardContent ? props.getUserCardContent() : <>
                        <h3>Your location</h3>
                    </>)}</div>
                }
                : undefined,
        }
        setUserMarkerProps(userMarkerProps);
    }

    const requirePrimaryMarkerProps = ():void =>
    {
        const lngLat = props.primaryMarker?.lngLat ?? null;
        if(primaryMarkerProps){
            if(!lngLat){
                primaryMarkerProps = null;
                setPopupPropsProps(null)
                return;
            }
            if(
                lngLat.lat === primaryMarkerProps.marker.latitude &&
                lngLat.lng === primaryMarkerProps.marker.longitude
            ){
                return;
            }
        } else {

            if(!lngLat){
                return;
            }
        }

        const cardContent = props.getPrimaryMarkerCardContent ? props.getPrimaryMarkerCardContent() : null
        const hasPopup = !props.getPrimaryMarkerCardContent || !!cardContent;
        primaryMarkerProps = {
            marker: {
                latitude: lngLat.lat,
                longitude: lngLat.lng,
            },
            popupContent: hasPopup ?
                () => {
                    return <div className="card-map">{(props.getPrimaryMarkerCardContent ? props.getPrimaryMarkerCardContent() : <>
                        <h3>{props.primaryMarker?.name ?? ""}</h3>
                    </>)}</div>
                }
                : undefined,
        }
        setPrimaryMarkerProps(primaryMarkerProps);
    }

    const requireMapClusterProps = ():void =>
    {
        let clusterProps:IMapClusterProps|null = null;
        if(Object.keys(clusterMarkersProps).length){

            const clusterWithUserCountryCode:string|null = countryCode ? countryCode : null;


            let clusterDidChange;
            if(mapClusterProps){
                clusterDidChange =
                    (Object.keys(clusterMarkersProps).length  !== Object.keys(mapClusterProps.markers!).length) ||
                    (clusterWithUserCountryCode !== mapClusterProps.withUser);

                if(!clusterDidChange){
                    for(let key in clusterMarkersProps){
                        const markerProps = clusterMarkersProps[key];
                        const checkMarkerProps = mapClusterProps.markers![key];
                        if(!mapClusterProps.markers![key]){
                            clusterDidChange = true;
                        } else if(
                            (markerProps.objectIDs!.length !== checkMarkerProps.objectIDs!.length) ||
                            (markerProps.outOfBounds !== checkMarkerProps.outOfBounds)
                        ){
                            clusterDidChange = true;
                        } else {
                            for(let objectID of markerProps.objectIDs!){
                                if(!checkMarkerProps.objectIDs!.includes(objectID)){
                                    clusterDidChange = true;
                                    break;
                                }
                            }
                        }
                        if(clusterDidChange){
                            break;
                        }
                    }
                }
            } else {
                clusterDidChange = true;
            }

            if(clusterDidChange){

                clusterProps = {
                    markers: clusterMarkersProps,
                    withCountry: true,
                    withUser: clusterWithUserCountryCode ?? undefined
                };
            } else {
                clusterProps = mapClusterProps
            }
        }
        if(clusterProps === mapClusterProps){
            return;
        }
        mapClusterProps = clusterProps;
        setMapClusterProps(clusterProps);
    }

    prepareContent();
    requirePopupProps();
    requireUserMarkerProps();
    requirePrimaryMarkerProps();
    requireMapClusterProps();

    return <>
        <section className={"content-block "+(!viewState.hasCurrentData ? "content-disabled" : "")}>
            <LocateMapForm
                routeProps={props}
                key={"&countryCode="+countryCode}
                postType={postType}
                countryChoiceList={countryChoiceList}
                countryCode={countryCode}
                onSearchSubmit={onSearchSubmit}
            />
        </section>
        <section className={"content-block "+(!viewState.hasCurrentData ? "content-disabled" : "")}>
            <div className="container">
                <div className="row">
                    <div className="col-12">

                        <MapLibreMap
                            routeProps={props}
                            ref={mapRef}
                            mapRef={mapRef}
                            countryCode={countryCode}
                            preferredMapFocus={preferredMapFocusType}
                            userMarker={userMarkerProps}
                            primaryMarker={primaryMarkerProps}
                            cluster={mapClusterProps}
                            popup={popupProps}
                            onMapInteraction={onMapInteraction}
                            onMarkerSelected={onMarkerSelected}
                        />
                    </div>
                </div>
                {!!getMap() && <>

                    <div className="row">
                        <div className="col-12 col-md-6">
                            {!!boundsChoiceOrder.length && <>
                                {boundsChoiceOrder.length > 1 && <>
                                    <AppButton
                                        className="btn mb-4"
                                        onClick={() => showPreviousEntry()}
                                    >
                                        Previous
                                    </AppButton>
                                </>}
                                <AppButton
                                    className="btn mb-4"
                                    onClick={() => showNextEntry()}
                                >
                                    {boundsChoiceOrder.length > 1 ? "Next" : "Show Location"}
                                </AppButton>
                            </>}
                        </div>
                        <div className="col-12 col-md-6 text-md-end">
                            <AppButton
                                className="btn mb-4"
                                disabled={getMap()!.isMapAtDefaultPosition()}
                                onClick={() => getMap()!.resetMapPosition()}
                            >
                                Reset
                            </AppButton>
                        </div>
                    </div>
                </>}
            </div>
        </section>
    </>
}

export default LocateMapPage;